"use client";
import { Collapse, Flex, Tag } from "antd";
import styles from "./booking.module.css";
import { IconChevronDown } from "@/lib/icons";
import dayjs from "dayjs";
import LocationButton from "../LocationButton";
import { useContext, useMemo } from "react";
import { locationcontext } from "@/lib/context";
import {
  deriveMultiDateCondition,
  getAvailableSteps,
  getNextStep,
} from "./stepFlow";
import {
  hasAnyActiveSeasonPasses,
  areAllSeasonPassesClosed,
} from "@/lib/seasonPassHelpers";
import SeasonPassOfferCard from "./SeasonPassOfferCard";

/** True when every child event under this venue has already ended. */
function isVenueCompleted(details) {
  const dates = details?.dates;
  if (!dates || typeof dates !== "object") {
    // Fall back to venue-level end window when dates map is missing.
    if (details?.end_date) {
      const endTime = details?.end_time || "23:59:59";
      return dayjs(
        `${details.end_date} ${endTime}`,
        "YYYY-MM-DD HH:mm:ss",
      ).isBefore(dayjs());
    }
    return false;
  }

  const allEvents = Object.entries(dates).flatMap(([date, dateData]) =>
    (dateData?.events || []).map((ev) => ({ ...ev, _date: date })),
  );

  if (allEvents.length === 0) {
    if (details?.end_date) {
      const endTime = details?.end_time || "23:59:59";
      return dayjs(
        `${details.end_date} ${endTime}`,
        "YYYY-MM-DD HH:mm:ss",
      ).isBefore(dayjs());
    }
    return false;
  }

  const now = dayjs();
  return allEvents.every((ev) => {
    const endDate = ev?.end_date || ev._date;
    const endTime = ev?.end_time || "23:59:59";
    return dayjs(`${endDate} ${endTime}`, "YYYY-MM-DD HH:mm:ss").isBefore(now);
  });
}

export default function VenueStep({
  data,
  setDates,
  setTickets,
  setActiveStep,
  orderDetails,
  setOrderDetails,
  availableSteps,
}) {
  const {
    id,
    name,
    cover_image,
    feature_images,
    banner_image,
    slug,
    show_single_registration,
    ticket_custom_field,
    is_custom_form,
    timezone,
    event_settings,
    seating_configuration,
    layout_type,
    sold_out,
    yet_to_be_announced,
    start_date,
    end_date,
    start_time,
    end_time,
    venue,
    season_tickets,
    is_contest,
    address,
    event_meta,
    tickets,
    is_zone_config,
    is_ticket_transferable,
    is_unique_registration,
    add_gst,
    genre_details,
    facilities,
    add_convenience_fees,
    show_available_tickets,
    currency,
    status,
    description,
    about,
    artists_details,
    social_media,
    category_details,
    organizer_details,
    terms_and_conditions,
    video_url,
    ...cities
  } = data;

  const { location } = useContext(locationcontext);

  // Show entry card on this screen immediately — no venue click required.
  const showSeasonPassOffer = useMemo(
    () => hasAnyActiveSeasonPasses(data),
    [data],
  );
  const seasonPassesClosed = useMemo(
    () => areAllSeasonPassesClosed(data),
    [data],
  );

  const selectVenue = (venueKey, details, isVenueTBA = false) => {
    const eventDates = details?.dates || {};
    const dateKeys = Object.keys(eventDates);
    const isSingleDate = dateKeys.length === 1;
    const firstDate = dateKeys[0];
    const firstEvent = eventDates[firstDate]?.events?.[0];
    const isSingleEvent =
      isSingleDate && eventDates[firstDate]?.events?.length === 1;

    const isMultiDate = deriveMultiDateCondition(eventDates);
    const isZone = isSingleEvent && Boolean(firstEvent?.is_zone_config);
    const isSitting =
      isSingleEvent && Boolean(firstEvent?.seating_configuration);

    const venueStepList = getAvailableSteps({
      isGroup: true,
      needsVenueSelection: true,
      isMultiDate,
      isZone,
      isSitting,
      haveConfiguredSeats: isSitting,
      bookingPath: "individual",
      seasonPassStepActive: false,
      slotShowId:
        isSingleEvent && firstEvent?.show_type === "recurring"
          ? firstEvent?.child_event_id
          : null,
    });
    const nextStep = getNextStep("venue", venueStepList) ?? "tickets";

    setActiveStep(nextStep);
    setDates(details?.dates);
    if (isSingleEvent) {
      setTickets(firstEvent?.tickets);
    }
    setOrderDetails((prev) => ({
      ...prev,
      parent_event_id: data?.id,
      venue: venueKey,
      city: details?.address?.city || null,
      timezone: firstEvent?.timezone || timezone,
      address: details?.address,
      yet_to_be_announced: isVenueTBA,
      date: isSingleEvent ? firstDate : null,
      time: isSingleEvent ? firstEvent?.start_time : null,
      end_date: isSingleEvent ? firstEvent?.end_date : null,
      end_time: isSingleEvent ? firstEvent?.end_time : null,
      event_id: isSingleEvent ? firstEvent?.child_event_id : null,
      layout_type: isSingleEvent ? firstEvent?.layout_type : null,
      is_zone_config: isSingleEvent ? firstEvent?.is_zone_config : null,
      seating_configuration: isSingleEvent
        ? firstEvent?.seating_configuration
        : null,
      ticket: null,
      zone: null,
      seating_layout_data: null,
      zone_seats: [],
      seating_layout_event_id: null,
      seating_layout_zone_id: null,
      session_id: null,
      session_created_at: null,
      booking_path: "individual",
      season_pass_step: false,
      is_season_pass: false,
      season_pass_entry: null,
      _venue_dates: details?.dates || null,
    }));
  };

  const openSeasonPasses = () => {
    setOrderDetails((prev) => ({
      ...prev,
      ticket: null,
      booking_path: null,
      season_pass_step: true,
      is_season_pass: false,
      season_pass_entry: "venue_screen",
    }));
    setActiveStep("season_passes");
  };

    /** True when API sold_out flag is set OR every ticket under the venue is exhausted. */
  function isVenueInventorySoldOut(details, citySoldOut = false) {
    if (citySoldOut || details?.sold_out) return true;

    const dates = details?.dates;
    if (!dates || typeof dates !== "object") return false;

    const tickets = Object.values(dates).flatMap((dateData) =>
      (dateData?.events || []).flatMap((ev) => ev?.tickets || []),
    );
    if (tickets.length === 0) return false;

    return tickets.every(
      (t) =>
        t?.is_sold_out === true ||
        t?.sold_out === true ||
        Number(t?.available ?? t?.available_quantity ?? 0) === 0,
    );
  }

  /** "sold_out" if every venue under a city is sold out, "completed" if every
   * venue has ended, otherwise null — used for the city-level badge. */
  function getCityStatus(venues) {
    const { sold_out, ...restSubVenues } = venues;
    const entries = Object.entries(restSubVenues);
    if (entries.length === 0) return null;

    if (
      entries.every(([, details]) =>
        isVenueInventorySoldOut(details, Boolean(sold_out)),
      )
    ) {
      return "sold_out";
    }
    if (entries.every(([, details]) => isVenueCompleted(details))) {
      return "completed";
    }
    return null;
  }

  function SubVenue({ venues, parentCity }) {
    const { sold_out, ...restSubVenues } = venues;
    return Object.entries(restSubVenues).map(([venueKey, details]) => {
      const completed = isVenueCompleted(details);
      const isSoldOut = isVenueInventorySoldOut(details, Boolean(sold_out));
      const disabled = isSoldOut || completed;

      const cityName = details?.address?.city || parentCity || "";
      const rawVenueName =
        details?.address?.name || details?.venue_name || venueKey || "";

      const isVenueTBA =
        Boolean(details?.yet_to_be_announced) ||
        rawVenueName.toLowerCase().includes("to be announced") ||
        venueKey.toLowerCase().includes("to be announced") ||
        !rawVenueName ||
        rawVenueName.trim().toLowerCase() === cityName.trim().toLowerCase();

      let venueDisplayName = "";
      if (isVenueTBA) {
        venueDisplayName = cityName
          ? `Venue to be announced, ${cityName}`
          : "Venue to be announced";
      } else {
        const cleanVenueName = rawVenueName.trim();
        const cleanCity = cityName.trim();
        if (
          cleanCity &&
          cleanVenueName.toLowerCase().endsWith(cleanCity.toLowerCase())
        ) {
          venueDisplayName = cleanVenueName;
        } else if (cleanCity) {
          venueDisplayName = `${cleanVenueName}, ${cleanCity}`;
        } else {
          venueDisplayName = cleanVenueName;
        }
      }

      return (
        <div
          className={`
  ${styles.subVenueCard} 
  w-full 
  mb-[15px]
  ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}
  ${orderDetails?.venue === venueKey && !disabled ? "!border-2 " : ""}
`}
          key={venueKey}
          aria-disabled={disabled}
          onClick={
            disabled
              ? undefined
              : () => selectVenue(venueKey, details, isVenueTBA)
          }
        >
          <Flex vertical gap={6} align="start">
            <div>{venueDisplayName}</div>
            <div className={styles.subVenueTiming}>
              {dayjs(details?.start_date).format("ddd D MMM")} -{" "}
              {dayjs(details?.end_date).format("ddd D MMM")}
            </div>
            {isSoldOut ? (
              <Tag
                color="red"
                className="!px-[20px] !py-[7px] !m-0"
                style={{
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: "#ffccc7",
                }}
              >
                Sold Out
              </Tag>
            ) : completed ? (
              <Tag
                color="blue"
                className="!px-[20px] !py-[7px] !m-0"
                style={{
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: "#91caff",
                }}
              >
                Completed
              </Tag>
            ) : null}
          </Flex>
          {!isSoldOut && !completed && !isVenueTBA && (
            <LocationButton place_id={details?.address?.google_place_id} />
          )}
        </div>
      );
    });
  }

  return (
    <div className={styles.venueSelection}>
      <h3 className={styles.sectionTitle2}>Select Venue</h3>
      <Flex vertical gap={16}>
        {showSeasonPassOffer && (
          <button
            type="button"
            onClick={
              seasonPassesClosed || data?.status === "completed"
                ? undefined
                : openSeasonPasses
            }
            disabled={seasonPassesClosed || data?.status === "completed"}
            aria-disabled={seasonPassesClosed || data?.status === "completed"}
            className={`w-full text-left border-0 bg-transparent p-0 ${
              seasonPassesClosed || data?.status === "completed"
                ? "cursor-not-allowed"
                : "cursor-pointer"
            }`}
          >
            <SeasonPassOfferCard
              title="Season Pass Available"
              labelsOnly={false}
              description="Cover multiple dates with one pass"
              closed={seasonPassesClosed || data?.status === "completed"}
            />
          </button>
        )}

        <div id="venue-list-anchor">
          <Flex vertical gap={16}>
            {Object.entries(cities)
              .filter(
                ([, v]) =>
                  v !== null &&
                  v !== undefined &&
                  typeof v === "object" &&
                  !Array.isArray(v),
              )
              .map(([city, venues]) => {
                const cityStatus = getCityStatus(venues);
                return (
                  <Collapse
                    key={city}
                    defaultActiveKey={
                      city.toLowerCase() === location?.toString().toLowerCase()
                        ? city
                        : ""
                    }
                    expandIcon={<IconChevronDown size={24} />}
                    style={{ cursor: "pointer" }}
                    items={[
                      {
                        key: city,
                        label: (
                          <Flex align="center" gap={8}>
                            <span>{city}</span>
                            {cityStatus === "sold_out" && (
                              <Tag
                                color="red"
                                className="!m-0"
                                style={{
                                  borderWidth: 1,
                                  borderStyle: "solid",
                                  borderColor: "#ffccc7",
                                }}
                              >
                                Sold Out
                              </Tag>
                            )}
                            {cityStatus === "completed" && (
                              <Tag
                                color="blue"
                                className="!m-0"
                                style={{
                                  borderWidth: 1,
                                  borderStyle: "solid",
                                  borderColor: "#91caff",
                                }}
                              >
                                Completed
                              </Tag>
                            )}
                          </Flex>
                        ),
                        children: (
                          <>
                            <SubVenue venues={venues} parentCity={city} />
                          </>
                        ),
                      },
                    ]}
                  />
                );
              })}
          </Flex>
        </div>
      </Flex>
    </div>
  );
}
